import "@/App.css";
import { useState, useCallback } from "react";
import Dashboard from "./pages/Dashboard";
import FewShots from "./pages/FewShots";
import AnalyticsCatalogue from "./pages/AnalyticsCatalogue";
import Metadata from "./pages/Metadata";
import AIChat from "./components/AIChat";
import { Toaster } from "./components/ui/sonner";
import { Zap, X, Plus } from "lucide-react";
import { cn } from "./lib/utils";

// Chrome-style tab component
function ChromeTab({ label, active, onClick, onClose, closeable = true }) {
  return (
    <div
      className={cn(
        "group relative flex items-center gap-2 px-4 py-2 cursor-pointer transition-all",
        "border-r border-[#1E293B] min-w-[140px] max-w-[200px]",
        active 
          ? "bg-[#0F172A] text-[#F1F5F9]" 
          : "bg-[#030712] text-[#64748B] hover:bg-[#0F172A]/50 hover:text-[#94A3B8]"
      )}
      onClick={onClick}
      data-testid={`tab-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <span className="text-sm truncate flex-1">{label}</span>
      {closeable && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose?.();
          }}
          className={cn(
            "w-4 h-4 rounded-sm flex items-center justify-center",
            "opacity-0 group-hover:opacity-100 transition-opacity",
            "hover:bg-[#334155]"
          )}
        >
          <X className="w-3 h-3" />
        </button>
      )}
      {/* Active indicator */}
      {active && (
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#0EA5E9]" />
      )}
    </div>
  );
}

function App() {
  const [activeTab, setActiveTab] = useState('agent-configurator');
  const [tabs, setTabs] = useState([
    { id: 'agent-configurator', label: 'Agent Configurator', closeable: false },
    { id: 'few-shots', label: 'Few-Shots', closeable: false },
    { id: 'analytics-catalogue', label: 'Analytics Catalogue', closeable: false },
    { id: 'metadata', label: 'Metadata', closeable: false }
  ]);
  const [chatOpen, setChatOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleCloseTab = (tabId) => {
    if (tabs.find(t => t.id === tabId)?.closeable === false) return;
    setTabs(prev => prev.filter(t => t.id !== tabId));
    if (activeTab === tabId) {
      setActiveTab(tabs[0].id);
    }
  };

  // Callback to refresh data in child components when AI makes changes
  const handleRefreshData = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  return (
    <div className="app-container h-screen flex flex-col bg-[#030712]">
      {/* Top Navigation Bar with Logo */}
      <header className="h-14 border-b border-[#1E293B] flex items-center justify-between px-4 bg-[#0F172A] shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-sm bg-[#0EA5E9] flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <h1 className="font-heading text-lg font-semibold text-white">PromptFlow</h1>
          <span className="text-xs text-[#64748B] font-mono">v1.0</span>
        </div>
      </header>

      {/* Chrome-style Tab Bar */}
      <div className="h-10 bg-[#030712] border-b border-[#1E293B] flex items-end shrink-0">
        <div className="flex items-end h-full">
          {tabs.map(tab => (
            <ChromeTab
              key={tab.id}
              label={tab.label}
              active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              onClose={() => handleCloseTab(tab.id)}
              closeable={tab.closeable}
            />
          ))}
        </div>
        {/* Add tab button (for future extensibility) */}
        <button
          className="h-full px-3 flex items-center justify-center text-[#64748B] hover:text-[#94A3B8] hover:bg-[#0F172A]/30 transition-colors"
          title="Add new tab"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'agent-configurator' && <Dashboard key={`dashboard-${refreshKey}`} />}
        {activeTab === 'few-shots' && <FewShots key={`fewshots-${refreshKey}`} />}
        {activeTab === 'analytics-catalogue' && <AnalyticsCatalogue key={`analytics-${refreshKey}`} />}
        {activeTab === 'metadata' && <Metadata key={`metadata-${refreshKey}`} />}
      </div>

      {/* AI Chat Widget */}
      <AIChat 
        isOpen={chatOpen} 
        onToggle={() => setChatOpen(!chatOpen)}
        onRefreshData={handleRefreshData}
      />

      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
}

export default App;
